import React, { useState } from 'react';
import { ChevronLeft } from '../../components/CustomIcons';
import { MODULES, LESSONS_BY_MODULE } from './learnerData';

/* ============================================================
   Shared UI primitives — consumed by all Prototype sub-views
   ============================================================ */

export const FALLBACK_GRADIENT =
  'linear-gradient(135deg, #0f2744 0%, #1e3a5f 55%, #f97316 130%)';

export const SafeImage = ({ src, alt, className = '', style = {}, fallbackIcon: Icon }) => {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div
        className={`${className} flex items-center justify-center relative overflow-hidden`}
        style={{ ...style, background: FALLBACK_GRADIENT }}
        role="img"
        aria-label={alt || 'Decorative illustration'}
      >
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25), transparent 40%), radial-gradient(circle at 80% 80%, rgba(249,115,22,0.35), transparent 45%)',
        }} />
        {Icon && (
          <div className="relative w-16 h-16 rounded-2xl bg-white/12 backdrop-blur-sm flex items-center justify-center border border-white/15">
            <Icon className="w-8 h-8 text-white/90" aria-hidden="true" />
          </div>
        )}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt || ''}
      className={className}
      style={style}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
};

export const Btn = ({ children, onClick, variant = 'primary', className = '', ...rest }) => {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-[12px] font-semibold text-[15px] transition-all active:scale-[0.98] min-h-[48px] px-6';
  const styles =
    variant === 'primary'
      ? 'bg-[#f97316] text-[#0f2744] hover:bg-[#fb923c]'
      : variant === 'secondary'
      ? 'bg-[#0f2744] text-[#fafaf9] hover:bg-[#081827]'
      : variant === 'ghost'
      ? 'bg-[#f1f5f9] text-[#0f2744] hover:bg-[#e2e8f0]'
      : 'bg-white text-[#0f2744] border-2 border-[#0f2744] hover:bg-[#0f2744] hover:text-[#fafaf9]';
  return (
    <button
      onClick={onClick}
      className={`${base} ${styles} ${className}`}
      style={{ fontFamily: 'var(--p-font-body)' }}
      {...rest}
    >
      {children}
    </button>
  );
};

export const Chip = ({ children, mono = false }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full bg-[#f1f5f9] text-[#0f2744] px-3 py-1 text-[13px] font-semibold"
    style={mono ? { fontFamily: 'var(--p-font-mono)' } : undefined}
  >
    {children}
  </span>
);

export const Mono = ({ children, className = '' }) => (
  <span className={`p-mono ${className}`}>{children}</span>
);

export const ProgressBar = ({ value, ariaLabel }) => (
  <div
    className="w-full h-2 rounded-full bg-[#e2e8f0] overflow-hidden"
    role="progressbar"
    aria-valuenow={Math.round(value)}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-label={ariaLabel || `Progress: ${Math.round(value)} percent`}
  >
    <div
      className="h-full rounded-full transition-[width] duration-700 ease-out bg-[#f97316]"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

export const Breadcrumbs = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 flex-wrap">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-[11px] text-[#94a3b8]" aria-hidden="true">›</span>}
          {it.onClick ? (
            <button
              onClick={it.onClick}
              data-testid={`proto-crumb-${it.label.replace(/\s+/g, '-').toLowerCase()}`}
              className="text-[11px] font-semibold uppercase tracking-widest hover:underline"
              style={{ color: '#64748b', letterSpacing: '0.06em' }}
            >
              {it.label}
            </button>
          ) : (
            <span
              className="text-[11px] font-semibold uppercase tracking-widest"
              style={{ color: '#0f2744', letterSpacing: '0.06em' }}
              aria-current="page"
            >
              {it.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export const ScreenHeader = ({ title, subtitle, onBack, breadcrumbs }) => (
  <header className="px-6 pt-4 pb-4 border-b border-[#e2e8f0] bg-[#fafaf9] sticky top-0 z-10">
    {(onBack || (breadcrumbs && breadcrumbs.length > 0)) && (
      <div className="flex items-center gap-2 mb-2 min-h-[24px]">
        {onBack && (
          <button
            onClick={onBack}
            data-testid="proto-back-btn"
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#0f2744] hover:bg-[#f1f5f9] shrink-0"
            aria-label="Go back to previous screen"
          >
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
        )}
        <Breadcrumbs items={breadcrumbs} />
      </div>
    )}
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <h1 className="p-display text-[22px] leading-tight truncate" style={{ margin: 0 }}>{title}</h1>
        {subtitle && (
          <p className="text-[13px] text-[#64748b] mt-0.5 truncate font-semibold uppercase tracking-wider" style={{ letterSpacing: '0.06em' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  </header>
);

export const PathwayRail = ({ moduleId, currentLessonIdx, completedLessons }) => {
  const module = MODULES.find((m) => m.id === moduleId);
  const lessons = LESSONS_BY_MODULE[moduleId] || [];
  const done = completedLessons || [];

  const nodes = [
    { key: 'enroll', label: 'Enrolled', done: true, current: false },
    ...lessons.map((l, i) => ({
      key: `l${i}`,
      label: l.title,
      done: done.includes(i),
      current: !done.includes(i) && i === currentLessonIdx,
    })),
    { key: 'cred', label: `${module?.credential || 'Credential'} earned`, done: done.length === lessons.length, current: false },
  ];

  const doneCount = nodes.filter((n) => n.done).length;
  const anchorIdx = Math.max(
    0,
    nodes.map((n, i) => (n.done || n.current ? i : -1)).filter((i) => i >= 0).pop() || 0,
  );
  const progressPct = (anchorIdx / (nodes.length - 1)) * 100;

  return (
    <div
      className="absolute left-3 top-[72px] bottom-[92px] w-4 pointer-events-none"
      role="progressbar"
      aria-valuenow={doneCount - 1}
      aria-valuemin={0}
      aria-valuemax={nodes.length - 1}
      aria-label={`Learning progress: ${Math.max(0, doneCount - 1)} of ${lessons.length} lessons complete`}
    >
      <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 rounded-full" style={{ background: '#e2e8f0' }} />
      <div
        className="absolute left-1/2 -translate-x-1/2 top-0 w-1 rounded-full transition-[height] duration-[600ms] ease-out"
        style={{ height: `${progressPct}%`, background: '#f97316' }}
      />
      {nodes.map((n, i) => {
        const topPct = (i / (nodes.length - 1)) * 100;
        const commonStyle = { top: `${topPct}%`, left: '50%' };
        if (n.current) {
          return (
            <div
              key={n.key}
              className="absolute w-3 h-3 rounded-full p-rail-node-current"
              style={{ ...commonStyle, background: '#f97316', transform: 'translate(-50%, -50%)' }}
              title={n.label}
            />
          );
        }
        if (n.done) {
          return (
            <div
              key={n.key}
              className="absolute w-3 h-3 rounded-full"
              style={{ ...commonStyle, background: '#f97316', transform: 'translate(-50%, -50%)' }}
              title={n.label}
            />
          );
        }
        return (
          <div
            key={n.key}
            className="absolute w-3 h-3 rounded-full border-2"
            style={{ ...commonStyle, background: '#fafaf9', borderColor: '#cbd5e1', transform: 'translate(-50%, -50%)' }}
            title={n.label}
          />
        );
      })}
    </div>
  );
};
